/**
 * humanize.js — converts raw PureSpectrum attribute text into plain,
 * Grade-8-reading-level buyer-brief language.
 *
 * PS's raw descriptions come as fragments like "I'm employed %%215%%",
 * "Which of the following languages can you read comprehensively? %%1511%%",
 * or "%%3232%%". We rewrite them into a consistent "I'm looking for people
 * that ..." brief-sentence pattern that reads naturally to a non-technical
 * client.
 *
 * Two exported functions:
 *   - humanizeTopic(rawDesc)        → the short title in the topic browser
 *   - humanizeQuestion(rawDesc)     → the full "I'm looking for people that…"
 *                                     sentence shown ABOVE the option list
 */

// Strip PureSpectrum's `%%digits%%` placeholder tokens + surplus whitespace.
function stripPlaceholders(s) {
  return String(s || "")
    .replace(/%%\d+%%/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

// Curated title map — the ~30 most common PS topics get a hand-crafted,
// friendly title. Unknown topics fall back to `_autoTitle`.
const TITLE_MAP = [
  { pattern: /which of the following languages can you read/i, title: "Languages Read" },
  { pattern: /pick the range that best describes.*total annual income/i, title: "Household Income" },
  { pattern: /what is your marital status/i, title: "Marital Status" },
  { pattern: /^I['']?m employed\b/i, title: "Employment Status" },
  { pattern: /highest level of education/i, title: "Education Level" },
  { pattern: /^Device\b/i, title: "Device Type" },
  { pattern: /^I['']?m a\b/i, title: "Gender" },
  { pattern: /^My age is\b/i, title: "Age" },
  { pattern: /children.*household|kids.*household/i, title: "Children in Household" },
  { pattern: /household size|how many people/i, title: "Household Size" },
  { pattern: /ethnicity|ethnic background/i, title: "Ethnicity" },
  { pattern: /hispanic/i, title: "Hispanic Origin" },
  { pattern: /which of the following industries.*work|i work in the.*industry|what industry|industry.*company.*work/i, title: "Industry" },
  { pattern: /company.*size|employees.*company/i, title: "Company Size" },
  { pattern: /(job title|role|role at work|position at)/i, title: "Job Role" },
  { pattern: /decision.?maker/i, title: "Decision-Maker Status" },
  { pattern: /(homeown|renter|own or rent)/i, title: "Home Ownership" },
  { pattern: /(automobile|vehicle).*own/i, title: "Vehicle Ownership" },
  { pattern: /new vehicle.*purchase|plan.*buy.*car/i, title: "In-Market Auto Shopper" },
  { pattern: /political party|voter registration/i, title: "Political Affiliation" },
  { pattern: /religion|religious/i, title: "Religion" },
  { pattern: /smoke|smoking/i, title: "Smoking Habits" },
  { pattern: /(alcoholic beverage|drink alcohol)/i, title: "Alcohol Consumption" },
  { pattern: /pet|dog or cat/i, title: "Pet Ownership" },
  { pattern: /(social media|instagram|tiktok|facebook)/i, title: "Social Media Usage" },
  { pattern: /streaming|netflix|prime video/i, title: "Streaming Services" },
  { pattern: /(travel|vacation).*past/i, title: "Recent Travel" },
  { pattern: /zip.?code/i, title: "ZIP / Postal Code" },
  { pattern: /country of birth|which country.*born/i, title: "Country of Birth" },
  { pattern: /(covid|vaccinat)/i, title: "Vaccination Status" },
  { pattern: /(diagnosed|health condition|medical condition)/i, title: "Health Condition" },
  { pattern: /(cook|cooking).*home/i, title: "Cooking at Home" },
];

/** Best-effort auto-title from a scrubbed question. */
function _autoTitle(scrubbed) {
  if (!scrubbed) return "Untitled filter";
  // Drop leading noise like "Which of the following", "What is your", etc.
  let s = scrubbed
    .replace(/^which of the following\s+/i, "")
    .replace(/^which is\s+/i, "")
    .replace(/^what (is|are) your\s+/i, "")
    .replace(/^please select\s+/i, "")
    .replace(/^i['']?m\s+/i, "")
    .replace(/^my\s+/i, "")
    .replace(/^do you\s+/i, "")
    .replace(/^have you\s+/i, "")
    .replace(/^are you\s+/i, "")
    .replace(/[?.]+$/g, "")
    .trim();
  if (!s) return scrubbed.slice(0, 42);
  // Title-case the first letter, keep the rest as-is.
  s = s.charAt(0).toUpperCase() + s.slice(1);
  return s.length > 48 ? s.slice(0, 48) + "…" : s;
}

/** Short, friendly topic title. */
export function humanizeTopic(rawDesc) {
  const scrubbed = stripPlaceholders(rawDesc);
  for (const { pattern, title } of TITLE_MAP) {
    if (pattern.test(scrubbed)) return title;
  }
  return _autoTitle(scrubbed);
}

// Curated "I'm looking for people that…" prompt map — hand-crafted sentences
// for the highest-value topics. Everything else falls back to `_autoPrompt`.
const PROMPT_MAP = [
  { pattern: /which of the following languages can you read/i,
    prompt: "I'm looking for people who can read these languages" },
  { pattern: /pick the range that best describes.*total annual income/i,
    prompt: "I'm looking for people whose household earns" },
  { pattern: /what is your marital status/i,
    prompt: "I'm looking for people who are" },
  { pattern: /^I['']?m employed\b/i,
    prompt: "I'm looking for people who work" },
  { pattern: /highest level of education/i,
    prompt: "I'm looking for people with this education level" },
  { pattern: /^Device\b/i,
    prompt: "I'm looking for people using this device" },
  { pattern: /children.*household|kids.*household/i,
    prompt: "I'm looking for people with children at home" },
  { pattern: /household size|how many people/i,
    prompt: "I'm looking for people whose household size is" },
  { pattern: /(ethnicity|ethnic background)/i,
    prompt: "I'm looking for people from this background" },
  { pattern: /hispanic/i,
    prompt: "I'm looking for people of Hispanic origin" },
  { pattern: /which of the following industries.*work|i work in the.*industry|what industry|industry.*company.*work/i,
    prompt: "I'm looking for people that work in this industry" },
  { pattern: /company.*size|employees.*company/i,
    prompt: "I'm looking for people at a company of this size" },
  { pattern: /(job title|role at work|position at)/i,
    prompt: "I'm looking for people with this job role" },
  { pattern: /decision.?maker/i,
    prompt: "I'm looking for decision-makers" },
  { pattern: /(homeown|renter|own or rent)/i,
    prompt: "I'm looking for people who" },
  { pattern: /(automobile|vehicle).*own/i,
    prompt: "I'm looking for people who own this type of vehicle" },
  { pattern: /new vehicle.*purchase|plan.*buy.*car/i,
    prompt: "I'm looking for people planning to buy a vehicle" },
  { pattern: /political party|voter registration/i,
    prompt: "I'm looking for people with this political affiliation" },
  { pattern: /religion|religious/i,
    prompt: "I'm looking for people of this religion" },
  { pattern: /smoke|smoking/i,
    prompt: "I'm looking for people with this smoking habit" },
  { pattern: /(alcoholic beverage|drink alcohol)/i,
    prompt: "I'm looking for people who drink these alcoholic beverages" },
  { pattern: /pet|dog or cat/i,
    prompt: "I'm looking for pet owners" },
  { pattern: /(social media|instagram|tiktok|facebook)/i,
    prompt: "I'm looking for people who use these platforms" },
  { pattern: /streaming|netflix|prime video/i,
    prompt: "I'm looking for people who use these streaming services" },
  { pattern: /(travel|vacation).*past/i,
    prompt: "I'm looking for people who travelled recently" },
  { pattern: /zip.?code/i,
    prompt: "I'm looking for people in this ZIP or postal code" },
  { pattern: /country of birth|which country.*born/i,
    prompt: "I'm looking for people born in this country" },
  { pattern: /(diagnosed|health condition|medical condition)/i,
    prompt: "I'm looking for people with this health condition" },
  { pattern: /(cook|cooking).*home/i,
    prompt: "I'm looking for people who cook at home" },
  { pattern: /^I['']?m a\b/i,
    prompt: "I'm looking for people of this gender" },
];

function _autoPrompt(scrubbed) {
  if (!scrubbed) return "I'm looking for people that match";
  // Question-style like "Which of the following …" or "What is your …":
  // rewrite to a brief-sentence.
  if (/^which of the following/i.test(scrubbed)) {
    return "I'm looking for people that match one of these";
  }
  if (/^what (is|are) your/i.test(scrubbed)) {
    const rest = scrubbed.replace(/^what (is|are) your\s+/i, "").replace(/[?.]$/, "").trim();
    return `I'm looking for people whose ${rest || "attribute"} is`;
  }
  if (/^what /i.test(scrubbed)) {
    return "I'm looking for people whose answer matches";
  }
  if (/^do you\b/i.test(scrubbed)) {
    return "I'm looking for people who do this";
  }
  if (/^have you\b/i.test(scrubbed)) {
    return "I'm looking for people who've done this";
  }
  if (/^are you\b/i.test(scrubbed)) {
    return "I'm looking for people who are this";
  }
  // Statement-style "I work in …", "I have …", "I own …" — rewrite the
  // first-person to a "people who …" clause.
  const iVerbMatch = scrubbed.match(/^i (?:'m |am )?(work|live|own|use|shop|drive|drink|eat|watch|play|read|travel|have)\b/i);
  if (iVerbMatch) {
    return `I'm looking for people who ${iVerbMatch[1].toLowerCase()}`;
  }
  if (/^i['']?m\s+/i.test(scrubbed)) {
    return `I'm looking for people who match this description`;
  }
  if (/^my\s+/i.test(scrubbed)) {
    const rest = scrubbed
      .replace(/^my\s+/i, "")
      .replace(/is\s*$/i, "")
      .replace(/[?.]+$/, "")
      .trim();
    return `I'm looking for people whose ${rest || "attribute"} is`;
  }
  // Generic fallback.
  return "I'm looking for people that match this attribute";
}

/** Full "I'm looking for people that …" sentence. */
export function humanizeQuestion(rawDesc) {
  const scrubbed = stripPlaceholders(rawDesc);
  for (const { pattern, prompt } of PROMPT_MAP) {
    if (pattern.test(scrubbed)) return prompt;
  }
  return _autoPrompt(scrubbed);
}
